import {
  FETCH_BOOKINGS,
  FETCH_BOOKINGS_SUCCESS,
  FETCH_BOOKINGS_FAILED,
  UPDATE_BOOKING,
  CANCEL_BOOKING
} from "../store/types";
import { fetchBookingLocations } from '../actions/locationactions';
import { RequestPushMsg } from '../other/NotificationFunctions';
import store from '../store/store';
import { firebase } from '../config/configureFirebase';
import { addActualsToBooking, saveAddresses, updateDriverQueue } from "../other/sharedFunctions";
import { get, onValue, update, off, remove, push } from "firebase/database";
import { uploadBytesResumable, getDownloadURL, uploadString } from "firebase/storage";
import getCurrentUid, { getNativeIdToken } from '../other/getCurrentUid';
import { safeUpdate, safeQueryRead } from '../other/firebaseDbHelper';

const getNativeStorage = () => {
  try {
    return require('@react-native-firebase/storage').default;
  } catch (error) {
    return null;
  }
};

const uriToUploadBlob = (uri) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.onload = () => resolve(xhr.response);
    xhr.onerror = () => reject(new Error("URI_TO_BLOB_FAILED"));
    xhr.responseType = "blob";
    xhr.open("GET", uri, true);
    xhr.send(null);
  });

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const waitForBookingAuthReady = async (auth, timeoutMs = 8000, intervalMs = 250) => {
  const deadline = Date.now() + timeoutMs;

  while (Date.now() < deadline) {
    if (auth?.currentUser) {
      return 'js';
    }

    const nativeToken = await getNativeIdToken();
    if (nativeToken) {
      return 'rest';
    }

    await delay(intervalMs);
  }

  return null;
};

const processBookingsData = (data, userInfo, dispatch) => {
  const active = [];
  let tracked = null;
  const bookings = Object.keys(data)
    .map((i) => {
      data[i].id = i;
      data[i].pickupAddress = data[i].pickup.add;
      data[i].dropAddress = data[i].drop.add;
      data[i].discount = data[i].discount ? data[i].discount : 0;
      data[i].cashPaymentAmount = data[i].cashPaymentAmount ? data[i].cashPaymentAmount : 0;
      data[i].cardPaymentAmount = data[i].cardPaymentAmount ? data[i].cardPaymentAmount : 0;
      return data[i];
    });
  for (let i = 0; i < bookings.length; i++) {
    if (['PAYMENT_PENDING','NEW', 'ACCEPTED', 'ARRIVED', 'STARTED', 'REACHED', 'PENDING', 'PAID'].indexOf(bookings[i].status) != -1) {
      active.push(bookings[i]);
    }
    if ((['ACCEPTED', 'ARRIVED', 'STARTED'].indexOf(bookings[i].status) != -1) && userInfo.usertype == 'driver') {
      tracked = bookings[i];
      fetchBookingLocations(tracked.id)(dispatch);
    }
  }
  dispatch({
    type: FETCH_BOOKINGS_SUCCESS,
    payload: {
      bookings: bookings.reverse(),
      active: active,
      tracked: tracked
    },
  });
};

export const fetchBookings = (retriesLeft = 2) => async (dispatch) => {

  const {
    bookingListRef,
    auth,
  } = firebase;

  dispatch({
    type: FETCH_BOOKINGS,
    payload: null,
  });

  const userInfo = store.getState().auth.profile;
  if (!userInfo?.uid || !userInfo?.usertype) {
    dispatch({
      type: FETCH_BOOKINGS_SUCCESS,
      payload: {
        bookings: [],
        active: [],
        tracked: null,
      },
    });
    return;
  }
  const emptyBookings = () => processBookingsData({}, userInfo, dispatch);

  // JS SDK auth available — use real-time listener
  if (auth?.currentUser) {
    off(bookingListRef(userInfo.uid, userInfo.usertype));
    onValue(bookingListRef(userInfo.uid, userInfo.usertype),(snapshot) => {
      if (snapshot.val()) {
        processBookingsData(snapshot.val(), userInfo, dispatch);
      } else {
        emptyBookings();
      }
    });
    return;
  }

  const authMode = await waitForBookingAuthReady(auth);

  if (authMode === 'js' && auth?.currentUser) {
    off(bookingListRef(userInfo.uid, userInfo.usertype));
    onValue(bookingListRef(userInfo.uid, userInfo.usertype),(snapshot) => {
      if (snapshot.val()) {
        processBookingsData(snapshot.val(), userInfo, dispatch);
      } else {
        emptyBookings();
      }
    });
    return;
  }

  // Fallback: REST API one-time read via native auth token
  const orderByField = userInfo.usertype === 'customer' ? 'customer'
    : userInfo.usertype === 'driver' ? 'driver'
    : userInfo.usertype === 'fleetadmin' ? 'fleetadmin'
    : null;

  if (!authMode) {
    if (retriesLeft > 0) {
      setTimeout(() => {
        dispatch(fetchBookings(retriesLeft - 1));
      }, 1000);
      return;
    }

    console.error('[bookings] Skipping fetch because auth never became ready.', {
      uid: userInfo?.uid || null,
      role: userInfo?.usertype || null,
    });
    dispatch({
      type: FETCH_BOOKINGS_FAILED,
      payload: store.getState().languagedata.defaultLanguage.no_bookings,
    });
    return;
  }

  if (orderByField) {
    safeQueryRead('/bookings', orderByField, userInfo.uid)
      .then((data) => {
        if (data) {
          processBookingsData(data, userInfo, dispatch);
        } else {
          emptyBookings();
        }
      })
      .catch((err) => {
        console.error('[bookings] Failed to fetch bookings.', {
          uid: userInfo?.uid || null,
          role: userInfo?.usertype || null,
          error: err?.message || String(err),
        });
        dispatch({
          type: FETCH_BOOKINGS_FAILED,
          payload: store.getState().languagedata.defaultLanguage.no_bookings,
        });
      });
  } else {
    dispatch({
      type: FETCH_BOOKINGS_FAILED,
      payload: store.getState().languagedata.defaultLanguage.no_bookings,
    });
  }
};

// Detaches the bookingList onValue listener for the current user.
// Dispatch from AuthLoading on logout (and from any component that owns a fetchBookings
// listener) so the persistent listener doesn't keep streaming subtree changes after the
// consumer is gone.
export const clearFetchBookings = () => (_dispatch) => {
  const { bookingListRef, auth } = firebase;
  const userInfo = store.getState().auth.profile;
  if (!userInfo?.uid || !userInfo?.usertype) {
    return;
  }
  if (auth?.currentUser) {
    off(bookingListRef(userInfo.uid, userInfo.usertype));
  }
};

// Card payment preparation is deliberately narrower than updateBooking. Older clients may
// still submit full booking snapshots, but current clients must never overwrite lifecycle or
// payment result fields immediately before handing the booking to the payment provider.
export const prepareBookingCardPayment = (booking) => async () => {
  if (!booking?.id) {
    throw new Error('Booking id is required for card payment preparation.');
  }
  if (!booking.paymentPacket) {
    throw new Error('Card payment details are required.');
  }

  const payload = {
    paymentPacket: booking.paymentPacket,
  };
  if (Object.prototype.hasOwnProperty.call(booking, 'preRequestedDrivers')) {
    payload.requestedDrivers = booking.preRequestedDrivers || {};
    payload.preRequestedDrivers = {};
  }

  await safeUpdate(firebase.singleBookingRef(booking.id), payload);
  return payload;
};

export const updateBooking = (booking) => async (dispatch) => {

  const {
    auth,
    trackingRef,
    singleBookingRef,
    singleUserRef,
    walletHistoryRef,
    settingsRef,
    userRatingsRef
  } = firebase;

  dispatch({
    type: UPDATE_BOOKING,
    payload: booking,
  });

  const settingsdata = await get(settingsRef);
  const settings = settingsdata.val();
  
  if (booking.status == 'PAYMENT_PENDING') {
    safeUpdate(singleBookingRef(booking.id),booking);
  }
  if (booking.status == 'NEW' || booking.status == 'ACCEPTED') {
    safeUpdate(singleBookingRef(booking.id), updateDriverQueue(booking));
  }
  if (booking.status == 'ARRIVED') {
    let dt = new Date();
    booking.driver_arrive_time = dt.getTime().toString();
    safeUpdate(singleBookingRef(booking.id),booking);
    if(booking.customer_token){
      RequestPushMsg(
        booking.customer_token,
        {
            title: store.getState().languagedata.defaultLanguage.notification_title,
            msg: store.getState().languagedata.defaultLanguage.driver_near,
            screen: 'BookedCab',
            params: { bookingId: booking.id }
        });
    }
  }
  
  if (booking.status == 'STARTED') {
    let dt = new Date();
    let localString = dt.getHours() + ":" + dt.getMinutes() + ":" + dt.getSeconds();
    let timeString = dt.getTime();
    booking.trip_start_time = localString;
    booking.startTime = timeString;
    safeUpdate(singleBookingRef(booking.id),booking);

    const driverLocation = store.getState().gpsdata.location;
    
    push(trackingRef(booking.id),{
      at: new Date().getTime(),
      status: 'STARTED',
      lat: driverLocation.lat,
      lng: driverLocation.lng
    });

    if(booking.customer_token){
      RequestPushMsg(
        booking.customer_token,
        {
            title: store.getState().languagedata.defaultLanguage.notification_title,
            msg: store.getState().languagedata.defaultLanguage.driver_journey_msg + booking.reference,
            screen: 'BookedCab',
            params: { bookingId: booking.id }
        });
      }
   }

  if (booking.status == 'REACHED') {

    const driverLocation = store.getState().gpsdata.location;

    push(trackingRef(booking.id),{
      at: new Date().getTime(),
      status: 'REACHED',
      lat: driverLocation.lat,
      lng: driverLocation.lng
    });

    let address = await saveAddresses(booking,driverLocation);

    let bookingObj = await addActualsToBooking(booking, address, driverLocation);
    safeUpdate(singleBookingRef(booking.id),bookingObj);

    if(booking.customer_token){
      RequestPushMsg(
        booking.customer_token,
        {
            title: store.getState().languagedata.defaultLanguage.notification_title,
            msg: store.getState().languagedata.defaultLanguage.driver_completed_ride,
            screen: 'BookedCab',
            params: { bookingId: booking.id }
        });
    }
  }

  if (booking.status == 'PENDING') {
    safeUpdate(singleBookingRef(booking.id),booking);
    safeUpdate(singleUserRef(booking.driver), { queue: false });
  }
  if (booking.status == 'PAID') {
      booking.status = 'COMPLETE';
    
    safeUpdate(singleBookingRef(booking.id), booking);
    if(booking.driver == getCurrentUid(auth) && (booking.prepaid || booking.payment_mode == 'cash' || booking.payment_mode == 'wallet')){
      safeUpdate(singleUserRef(booking.driver), { queue: false });
    }

    if(booking.customer_token){
      RequestPushMsg(
        booking.customer_token,
        {
            title: store.getState().languagedata.defaultLanguage.notification_title,
            msg: store.getState().languagedata.defaultLanguage.success_payment,
            screen: 'BookedCab',
            params: { bookingId: booking.id }
        });
    }

    if(booking.driver_token){
        RequestPushMsg(
          booking.driver_token,
          {
              title: store.getState().languagedata.defaultLanguage.notification_title,
              msg: store.getState().languagedata.defaultLanguage.success_payment,
              screen: 'BookedCab',
              params: { bookingId: booking.id }
          });
      }
    }

  if (booking.status == 'COMPLETE') {
    const parsedRating = Number(booking.rating);
    const hasSubmittedRating = Number.isFinite(parsedRating) && parsedRating > 0;

    safeUpdate(singleBookingRef(booking.id), booking);
    if (!hasSubmittedRating) {
      return;
    }

      if(booking.driver_token){
        const languageData = store.getState().languagedata;
        const defaultLanguage = languageData && languageData.defaultLanguage;
        const receivedRating = defaultLanguage && defaultLanguage.received_rating;
        
        let message = '';
        if (receivedRating) {
          message = receivedRating.toString().replace("X", parsedRating.toString());
        } else {
          message = `Rating: ${parsedRating}`;
        }
        
        RequestPushMsg(
          booking.driver_token,
          {
              title: defaultLanguage && defaultLanguage.notification_title ? 
                     defaultLanguage.notification_title : 'Notification',
              msg: message,
              screen: 'BookedCab',
              params: { bookingId: booking.id }
          });
      }
      onValue(userRatingsRef(booking.driver), snapshot => {
        let ratings = snapshot.val();
        let rating;
        if(ratings){
          let sum = 0;
          const arr = Object.values(ratings);
          for (let i = 0; i< arr.length ; i++){
            sum = sum + arr[i].rate
          }
          sum = sum + parsedRating;
          rating = parseFloat(sum / (arr.length + 1)).toFixed(1);
        }else{
          rating = parsedRating;
        }
        safeUpdate(singleUserRef(booking.driver),{rating: rating});
        push(userRatingsRef(booking.driver), {
          user: booking.customer,
          rate: parsedRating
        });
      }, { onlyOnce: true});
    
  }
};

export const cancelBooking = (data) => (dispatch) => {
  const {
    singleBookingRef,
    singleUserRef,
    requestedDriversRef
  } = firebase;

  dispatch({
    type: CANCEL_BOOKING,
    payload: data,
  });


  const isCustomerCancel = data.cancelledBy === 'customer';
  const isDriverCancel = data.cancelledBy === 'driver';

  // --- Sürücü İptali (reBook = true) --- 
  if (isDriverCancel) {
    safeUpdate(singleBookingRef(data.booking.id), {
      status: 'NEW', // Tekrar sürücü aramaya uygun hale getir
      driver: null,
      driver_token: null,
      driver_image: null,
      driver_name: null,
      driver_contact: null,
      driver_car_no: null,
      vehicleModel: null,
      vehicleMake: null,
      driverRating: null,
      fleetadmin: null,
      fleetCommission: null,
      driverDeviceId: null,
      reason: data.reason,
      cancelledBy: data.cancelledBy
    }).then(() => {
      // Sürücüyü tekrar müsait yap
      if (data.booking.driver) {
        safeUpdate(singleUserRef(data.booking.driver), { queue: false });
      }
      // Müşteriye iptal bildirimi gönder
      if (data.booking.customer_token) {
        RequestPushMsg(
          data.booking.customer_token,
          {
            title: store.getState().languagedata.defaultLanguage.notification_title,
            msg: store.getState().languagedata.defaultLanguage.booking_cancelled_by_driver + data.booking.id,
            screen: 'BookedCab',
            params: { bookingId: data.booking.id }
          }
        );
      }
    }).catch(error => {
      console.error("Error updating booking for driver cancellation:", error);
    });
  } 
  // --- Müşteri İptali veya Diğer Durumlar --- 
  else {
    // The refund decision has to land in the SAME update as the CANCELLED status: the
    // booking trigger reacts to the status change, so writing the flag afterwards would let
    // the automatic refund fire first. Only an explicit `refund === false` sets it; a normal
    // cancellation keeps today's behaviour and still refunds automatically.
    const cancelPatch = {
      status: 'CANCELLED',
      reason: data.reason,
      cancelledBy: data.cancelledBy
    };
    if (data.refund === false) {
      cancelPatch.refundOnCancel = false;
      cancelPatch.refundDecisionBy = data.decidedBy || null;
    }
    safeUpdate(singleBookingRef(data.booking.id), cancelPatch).then(() => {
      // Eğer bir sürücü atanmışsa ve görevdeyse, sürücüyü tekrar müsait yap
      if (data.booking.driver && (data.booking.status === 'ACCEPTED' || data.booking.status === 'ARRIVED')) {
        safeUpdate(singleUserRef(data.booking.driver), { queue: false });
        
        // Sürücüye iptal bildirimi gönder
        if (data.booking.driver_token) {
          RequestPushMsg(
            data.booking.driver_token,
            {
              title: store.getState().languagedata.defaultLanguage.notification_title,
              msg: store.getState().languagedata.defaultLanguage.booking_cancelled_by_customer + data.booking.id,
              screen: 'BookedCab',
              params: { bookingId: data.booking.id }
            }
          );
        }
      }
      // Eğer booking 'NEW' durumunda ise ve sürücü aranıyorsa, isteği kaldır
      if (data.booking.status === 'NEW') {
        remove(requestedDriversRef(data.booking.id)).then(() => {
        }).catch(error => {
            console.error(`Error removing requested drivers for booking ${data.booking.id}:`, error);
        });
      }
    }).catch(error => {
      console.error("Error updating booking for customer cancellation:", error);
    });
  }
};

export const updateBookingImage = (booking, imageType, imageBlob) => async (dispatch) => {
  const   {
    singleBookingRef,
    bookingImageRef
  } = firebase;
  const updatedBooking = { ...booking };
  const imageRef = bookingImageRef(updatedBooking.id,imageType);
  const nativeStorage = getNativeStorage();
  const uploadMode = nativeStorage && imageBlob?.uri ? 'native' : 'web';

  try {
    let url;

    if (uploadMode === 'native') {
      const nativeRef = nativeStorage().ref(`bookings/${updatedBooking.id}/${imageType}`);
      await nativeRef.putFile(imageBlob.uri, {
        contentType: imageBlob.mimeType || 'image/jpeg',
      });
      url = await nativeRef.getDownloadURL();
    } else if (imageBlob?.base64) {
      await uploadString(imageRef, imageBlob.base64, 'base64', {
        contentType: imageBlob.mimeType || 'image/jpeg',
      });
    } else if (imageBlob?.uri) {
      const uploadBlob = await uriToUploadBlob(imageBlob.uri);
      await uploadBytesResumable(imageRef, uploadBlob, {
        contentType: imageBlob.mimeType || 'image/jpeg',
      });
      uploadBlob?.close?.();
    } else {
      await uploadBytesResumable(imageRef, imageBlob);
      imageBlob.close?.();
    }

    if (!url) {
      url = await getDownloadURL(imageRef);
    }
    if(imageType == 'pickup_image'){
      updatedBooking.pickup_image = url;
    }
    if(imageType == 'deliver_image'){
      updatedBooking.deliver_image = url;
    }
    await safeUpdate(singleBookingRef(updatedBooking.id), { [imageType]: url });
    dispatch({
      type: UPDATE_BOOKING,
      payload: updatedBooking,
    });
    return updatedBooking;
  } catch (error) {
    imageBlob?.close?.();
    console.error('[booking-image] Upload failed.', {
      bookingId: booking?.id || null,
      imageType,
      uploadMode,
      error: error?.message || String(error),
    });
    throw error;
  }
};

export const forceEndBooking = (booking) => async (dispatch) => {

  const {
    trackingRef,
    singleBookingRef,
    singleUserRef,
    walletHistoryRef,
    settingsRef,
  } = firebase;

  dispatch({
    type: UPDATE_BOOKING,
    payload: booking,
  });
  
  if (booking.status == 'STARTED') {

    push(trackingRef(booking.id),{
      at: new Date().getTime(),
      status: 'REACHED',
      lat: booking.drop.lat,
      lng: booking.drop.lng
    });

    const end_time = new Date();
    const diff = (end_time.getTime() - parseFloat(booking.startTime)) / 1000;
    const totalTimeTaken = Math.abs(Math.round(diff));
    booking.trip_end_time = end_time.getHours() + ":" + end_time.getMinutes() + ":" + end_time.getSeconds();
    booking.endTime = end_time.getTime();
    booking.total_trip_time = totalTimeTaken;

    if(booking.customer_token){
      RequestPushMsg(
        booking.customer_token,
        {
            title: store.getState().languagedata.defaultLanguage.notification_title,
            msg: store.getState().languagedata.defaultLanguage.driver_completed_ride,
            screen: 'BookedCab',
            params: { bookingId: booking.id }
        });
    }

    safeUpdate(singleUserRef(booking.driver),{ queue: false });

    // Ödeme işlemi Firebase Functions tarafından otomatik olarak yapılacak
    // Burada manuel ödeme yapmıyoruz çünkü çift ödeme sorununa neden oluyor
    if (booking.prepaid) {
      booking.status = 'PAID';
    } else {
      booking.status = 'PENDING';
    }

    safeUpdate(singleBookingRef(booking.id), booking);
  }
};
